import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Renders the reporting engine's chart block (bar or line) from data
// attributes — the agent picks the chart type and which table columns
// feed it; this controller only draws.
//
// data-report-chart-config-value: {
//   type: "bar" | "line",
//   labels: [...],        // x-axis values
//   values: [...],        // numeric series
//   seriesName: "..."     // y-axis label
// }
export default class extends Controller {
  static values = { config: Object }

  connect() {
    const { type, labels, values, seriesName } = this.configValue

    // In the dedicated print document, render at a fixed width that fits
    // inside the card on paper — sizing at print time is guesswork.
    this.printMode = document.body.classList.contains("report-print")
    const width = this.printMode ? 640 : "100%"
    if (!this.printMode) this.registerPrintHandlers()

    if (type === "pie") {
      this.chart = new ApexCharts(this.element, {
        chart: { type: "donut", width, height: 300, fontFamily: "inherit", animations: { enabled: false } },
        series: values,
        labels: labels,
        legend: { position: "right", fontSize: "12px" },
        dataLabels: {
          enabled: true,
          formatter: (percent) => `${Math.round(percent)}%`
        },
        tooltip: {
          y: { formatter: (v) => (typeof v === "number" ? v.toLocaleString("en-GB", { maximumFractionDigits: 2 }) : v) }
        },
        stroke: { width: 1 }
      })
      this.chart.render()
      return
    }

    this.chart = new ApexCharts(this.element, {
      chart: {
        type: type === "line" ? "line" : "bar",
        width,
        height: 280,
        toolbar: { show: false },
        fontFamily: "inherit",
        animations: { enabled: false }
      },
      series: [{ name: seriesName || "Value", data: values }],
      xaxis: {
        categories: labels,
        labels: {
          style: { fontSize: "11px" },
          // Every bar is a distinct entity (a landlord, a contractor…) —
          // never silently drop its label; rotate consistently and trim.
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: true,
          maxHeight: 100
        }
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px" },
          formatter: (v) => (typeof v === "number" ? v.toLocaleString("en-GB", { maximumFractionDigits: 2 }) : v)
        }
      },
      colors: ["#4f46e5"],
      dataLabels: { enabled: false },
      stroke: type === "line" ? { curve: "smooth", width: 3 } : { show: false },
      plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
      grid: { borderColor: "#f3f4f6" },
      tooltip: {
        y: { formatter: (v) => (typeof v === "number" ? v.toLocaleString("en-GB", { maximumFractionDigits: 2 }) : v) }
      }
    })
    this.chart.render()
  }

  disconnect() {
    window.removeEventListener("beforeprint", this.shrinkForPrint)
    window.removeEventListener("afterprint", this.restoreAfterPrint)
    this.chart?.destroy()
  }

  // ApexCharts renders a fixed-width SVG that overflows A4 — re-render to
  // paper width for the print dialog, then restore.
  registerPrintHandlers() {
    // Fallback for printing the normal app page directly (Cmd+P).
    this.shrinkForPrint = () => this.chart?.updateOptions({ chart: { width: 640 } }, false, false)
    this.restoreAfterPrint = () => this.chart?.updateOptions({ chart: { width: "100%" } }, false, false)
    window.addEventListener("beforeprint", this.shrinkForPrint)
    window.addEventListener("afterprint", this.restoreAfterPrint)
  }
}
